import { aircraftQuery } from "@common/api/aircraft"
import type { AtlPagesParams } from "@common/api/atl"
import type { AtlPageStatus } from "@common/client"
import type { FilterFieldConfig } from "@common/components/filters/filter-config"
import { useQuery } from "@tanstack/react-query"
import { CircleDot, PenLine, Plane, Tag, UserRound } from "lucide-react"
import { z } from "zod"
import { FLEET } from "@/routes/_layout/aircraft/-lib/search"
import { techLogSearchFields } from "@/routes/_layout/aircraft_/$aircraftId/-lib/tech-log"
import { usePeopleOptions } from "@/routes/_layout/aircraft_/$aircraftId/-lib/tech-log-filters"
import {
  type Direction,
  directionSchema,
  withFixed,
} from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/table"
import {
  COLUMNS,
  type Column,
  columnSchema,
  type Grouping,
  groupChoiceSchema,
  groupingSchema,
  type Sort,
  sortSchema,
} from "./columns"

export const STATUSES = [
  "draft",
  "recorded",
] as const satisfies readonly AtlPageStatus[]

const statusSchema = z.enum(STATUSES)

export const techLogsSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  status: z.array(statusSchema).optional().catch(undefined),
  aircraft: z.array(z.string()).optional().catch(undefined),
  model: z.array(z.string()).optional().catch(undefined),
  entered_by: z.array(z.string()).optional().catch(undefined),
  updated_by: z.array(z.string()).optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: sortSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
  group: groupChoiceSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  log_tab: techLogSearchFields.log_tab,
  log_scan: techLogSearchFields.log_scan,
  log_view: techLogSearchFields.log_view,
  wide: techLogSearchFields.wide,
})

export type TechLogsSearch = z.infer<typeof techLogsSearchSchema>

export type TechLogsFilterValues = Pick<
  TechLogsSearch,
  "status" | "aircraft" | "model" | "entered_by" | "updated_by"
>

export const NO_TECH_LOGS_FILTERS: TechLogsFilterValues = {
  status: undefined,
  aircraft: undefined,
  model: undefined,
  entered_by: undefined,
  updated_by: undefined,
}

export function hasTechLogsFilters(values: TechLogsFilterValues): boolean {
  return Object.values({
    status: values.status,
    aircraft: values.aircraft,
    model: values.model,
    entered_by: values.entered_by,
    updated_by: values.updated_by,
  }).some((selected) => Boolean(selected?.length))
}

export function statusLabel(status: AtlPageStatus): string {
  switch (status) {
    case "draft":
      return "Drafts"
    case "recorded":
      return "Recorded"
  }
}

export type TechLogsDisplay = {
  columns: Column[]
  sort: Sort
  direction: Direction
  group: Grouping | undefined
  subgroup: Grouping | undefined
}

export function displayOf(search: TechLogsSearch): TechLogsDisplay {
  const group = search.group === "none" ? undefined : (search.group ?? "status")
  return {
    columns: search.columns
      ? withFixed(COLUMNS, "page", search.columns)
      : [...COLUMNS],
    sort: search.sort ?? "date",
    direction: search.direction ?? "desc",
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
  }
}

export function nextSort(
  display: TechLogsDisplay,
  column: Sort,
): Pick<TechLogsSearch, "sort" | "direction"> {
  const direction =
    column === display.sort && display.direction === "desc" ? "asc" : "desc"
  return column === "date" && direction === "desc"
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

export function techLogsParams(search: TechLogsSearch): AtlPagesParams {
  const display = displayOf(search)
  return {
    status: search.status,
    aircraft_id: search.aircraft,
    model: search.model,
    created_by_id: search.entered_by,
    last_modified_by_id: search.updated_by,
    search: search.search,
    sort: display.sort === "page" ? "log_number" : "flight_date",
    direction: display.direction,
    group: display.group,
    subgroup: display.subgroup,
  }
}

function useFleetOptions() {
  const { data, isLoading } = useQuery(aircraftQuery(FLEET))
  return {
    options: (data?.data ?? []).map((aircraft) => ({
      value: aircraft.id,
      label: aircraft.registration_no,
      sublabel: aircraft.model,
    })),
    isLoading,
  }
}

function useModelOptions() {
  const { data, isLoading } = useQuery(aircraftQuery(FLEET))
  const models = [...new Set((data?.data ?? []).map(({ model }) => model))]
  return {
    options: models.sort().map((model) => ({ value: model, label: model })),
    isLoading,
  }
}

export const techLogsFilterConfigs: FilterFieldConfig<TechLogsFilterValues>[] =
  [
    {
      key: "status",
      label: "Status",
      icon: CircleDot,
      type: "multiselect",
      searchPlaceholder: "Search statuses",
      emptyLabel: "No statuses",
      useOptions: () => ({
        options: STATUSES.map((status) => ({
          value: status,
          label: statusLabel(status),
        })),
        isLoading: false,
      }),
      get: (values) => values.status,
      set: (next) => ({
        status: next?.flatMap((value) => {
          const parsed = statusSchema.safeParse(value)
          return parsed.success ? [parsed.data] : []
        }),
      }),
    },
    {
      key: "aircraft",
      label: "Aircraft",
      icon: Plane,
      type: "multiselect",
      searchPlaceholder: "Search aircraft",
      emptyLabel: "No aircraft",
      useOptions: useFleetOptions,
      get: (values) => values.aircraft,
      set: (next) => ({ aircraft: next?.map(String) }),
    },
    {
      key: "model",
      label: "Aircraft type",
      icon: Tag,
      type: "multiselect",
      searchPlaceholder: "Search types",
      emptyLabel: "No types",
      useOptions: useModelOptions,
      get: (values) => values.model,
      set: (next) => ({ model: next?.map(String) }),
    },
    {
      key: "entered_by",
      label: "Entered by",
      icon: UserRound,
      type: "multiselect",
      searchPlaceholder: "Search people",
      emptyLabel: "No people",
      useOptions: usePeopleOptions,
      get: (values) => values.entered_by,
      set: (next) => ({ entered_by: next?.map(String) }),
    },
    {
      key: "updated_by",
      label: "Last updated by",
      icon: PenLine,
      type: "multiselect",
      searchPlaceholder: "Search people",
      emptyLabel: "No people",
      useOptions: usePeopleOptions,
      get: (values) => values.updated_by,
      set: (next) => ({ updated_by: next?.map(String) }),
    },
  ]
